import { useCallback, useRef } from 'react';
import { FieldPath, FieldValues, useWatch } from 'react-hook-form';

import { BusinessPartnerDto, useProductCenterApi } from 'api';
import {
	AutocompleteAsYouType,
	AutocompleteAsYouTypeProps,
} from 'library/components/controls/form/AutocompleteAsYouType';

export type BusinessPartnerPickerProps<
	TFieldValues extends FieldValues = FieldValues,
	TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = Omit<
	AutocompleteAsYouTypeProps<BusinessPartnerDto, TFieldValues, TName>,
	'fetchOptions' | 'getOptionLabel' | 'getOptionId' | 'isOptionEqualToValue'
>;

export function BusinessPartnerPicker<
	TFieldValues extends FieldValues = FieldValues,
	TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
	control,
	name,
	...props
}: BusinessPartnerPickerProps<TFieldValues, TName>): React.JSX.Element {
	const { api } = useProductCenterApi();
	const selectedOption = useRef<BusinessPartnerDto | null>(null);
	const selectedId = useWatch({ control, name });

	const fetchBusinessPartners = useCallback(
		(input: string, callback: (value: BusinessPartnerDto[]) => void) => {
			void api
				.searchBusinessPartners({ prompt: input })
				.then((response) => {
					callback(response.data);
				});
		},
		[api]
	);

	const fetchWithSelectedKept = useCallback(
		(input: string, callback: (value: BusinessPartnerDto[]) => void) => {
			fetchBusinessPartners(input, (results) => {
				if (
					selectedOption.current &&
					!results.some(
						(r) => r.customerNumber.toString() === selectedId
					)
				) {
					callback([selectedOption.current, ...results]);
				} else {
					callback(results);
				}
			});
		},
		[fetchBusinessPartners, selectedId]
	);

	const isOptionEqualToValue = useCallback(
		(option: BusinessPartnerDto, value: BusinessPartnerDto) => {
			const match =
				option.customerNumber.toString() ===
				(typeof value !== 'object'
					? value
					: value?.customerNumber.toString());
			if (match) {
				selectedOption.current = option;
			}
			return match;
		},
		[]
	);

	const getOptionLabel = useCallback(
		(option: BusinessPartnerDto) =>
			`${option.customerNumber} - ${option.customerName}`,
		[]
	);

	const getOptionId = useCallback(
		(option: BusinessPartnerDto) => option.customerNumber.toString(),
		[]
	);

	return (
		<AutocompleteAsYouType<BusinessPartnerDto, TFieldValues, TName>
			control={control}
			name={name}
			fetchOptions={fetchWithSelectedKept}
			getOptionLabel={getOptionLabel}
			getOptionId={getOptionId}
			isOptionEqualToValue={isOptionEqualToValue}
			{...props}
		/>
	);
}
